import { Dialog, DialogContent, View, Image } from "@/components/ui";
import type { PackageDetailFragmentFragment } from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageDetailSortByEnum,
  PackageRecurrenceEnum,
  PackageUnitTypeEnum,
  SortEnum,
} from "@/__generated__/graphql";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import { useEffect, useMemo, useState } from "react";
import { useBookingState } from "@/store/booking/useBookingState";
import { BookingItemCard } from "@/components/shared/booking/booking-item-card";
import { formatPrice } from "@/lib/helpers/number";
import { ListHeading } from "@/components/shared/list-header";
import { Cube } from "@/components/shared/icons";
import { Message } from "@/components/shared/message";
import { Spin } from "@/components/shared/spin";
import {
  getCarpetUpholsteryFromUnit,
  getCurtainCleaningFromCode,
  getUpholsteryServicesType,
} from "@/constants/booking";
import type { UpholsteryServiceName } from "@/types/service";
import { getServiceUnit } from "@/lib/booking-lib";
import CurtainItemInfo from "@/assets/images/curtain-item-info-image.png";
import { Typography } from "@/components/shared/typography";

export type UpholsteryPackage = PackageDetailFragmentFragment & {
  recommended?: boolean;
  description?: string;
  title: string;
  img: string;
  sortOrder?: number;
  maxItem: number;
};

export const UpholsteryCleaningBooking = ({
  name,
}: {
  name: UpholsteryServiceName;
}) => {
  const {
    data: { packageDetails, selectedPackageIds },
    setBookingState,
  } = useBookingState();
  const { fetch: getAllClientPackageDetails, loading } =
    useAllClientPackageDetailsStore();

  const [showItemInfo, setShowItemInfo] = useState<boolean>(false);

  const isCurtainBooking = name === "curtain-cleaning";
  const isMattressBooking = name === "mattress-cleaning";
  const isSofaBooking = name === "sofa-cleaning";
  const isRugBooking = name === "rug-cleaning";
  const isPramSeatBooking = name === "pram-seat";

  const upholsteryUnitType = useMemo(() => {
    if (isMattressBooking) {
      return [PackageUnitTypeEnum.Mattress];
    }
    if (isSofaBooking) {
      return [PackageUnitTypeEnum.Sofa];
    }
    if (isRugBooking) {
      return [PackageUnitTypeEnum.Rug];
    }
    if (isCurtainBooking) {
      return [PackageUnitTypeEnum.Curtain];
    }
    if (isPramSeatBooking) {
      return [PackageUnitTypeEnum.BabyPram, PackageUnitTypeEnum.ChildSeat];
    }
    return [];
  }, [name]);

  useEffect(() => {
    getAllClientPackageDetails({
      requestPayload: {
        filters: {
          active: true,
          department: PackageDepartmentEnum.DeepCleaning,
          sort: [SortEnum.Asc],
          sortBy: [PackageDetailSortByEnum.Duration],
          serviceTypes: [getUpholsteryServicesType(name)],
          unitTypes: [
            PackageUnitTypeEnum.Mattress,
            PackageUnitTypeEnum.Sofa,
            PackageUnitTypeEnum.Rug,
            PackageUnitTypeEnum.ChildSeat,
            PackageUnitTypeEnum.BabyPram,
            PackageUnitTypeEnum.Carpet,
            PackageUnitTypeEnum.Curtain,
          ],
        },
      },
    }).then((res) => {
      if (res.error || !res.data) {
        return;
      }
      setBookingState({
        packageDetails: res.data,
        packageDetailsFilter: {
          repeatEvery: [PackageRecurrenceEnum.AdHoc],
        },
      });
    });
  }, []);

  const filteredPackages: UpholsteryPackage[] = useMemo(() => {
    if (!packageDetails) return [];
    if (isCurtainBooking) {
      return packageDetails
        .map(
          (pkg) =>
            ({
              ...pkg,
              ...getCurtainCleaningFromCode(pkg.code),
            }) as UpholsteryPackage,
        )
        .filter(
          (pkg) => !!pkg.title && upholsteryUnitType.includes(pkg.unitType),
        )
        .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0));
    } else {
      return packageDetails
        .map(
          (pkg) =>
            ({
              ...pkg,
              ...getCarpetUpholsteryFromUnit(pkg.unitType, pkg.unitSize),
            }) as UpholsteryPackage,
        )
        .filter(
          (pkg) => !!pkg.title && upholsteryUnitType.includes(pkg.unitType),
        )
        .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0));
    }
  }, [packageDetails, name, upholsteryUnitType]);

  const getSelectedPackageValue = (id: string) => {
    return selectedPackageIds.filter((pkgId) => pkgId === id).length || 0;
  };

  const onAdd = (id: string) => {
    setBookingState({
      selectedPackageIds: [...selectedPackageIds, id],
    });
  };

  const onMinus = (id: string) => {
    selectedPackageIds.splice(selectedPackageIds.indexOf(id), 1);
    setBookingState({
      selectedPackageIds,
    });
  };

  const upholsteryHeaderMessage = isCurtainBooking
    ? "A minimum order of $100 is required to proceed."
    : "Get 5% discount per additional item up to 15% off!";

  return (
    <>
      <View>
        <View className="mx-4 py-4">
          <ListHeading title="Add Item Type" icon={Cube} />
          {isCurtainBooking && (
            <Typography
              variant="body-md"
              color="foreground-intermediate"
              className="mt-2"
            >
              Each curtain is priced individually per piece.
            </Typography>
          )}
          <Message
            variant="info"
            message={upholsteryHeaderMessage}
            className="mt-4"
          />

          {loading && <Spin className="native:p-12 m-12 size-8" />}
          {filteredPackages.map((pkg) => (
            <BookingItemCard
              key={pkg.id}
              title={pkg.title}
              subTitle={pkg?.description || ""}
              itemPrice={`${formatPrice(pkg.unitValue)}`}
              itemUnit={`/${getServiceUnit(name)}`}
              selected={getSelectedPackageValue(pkg.id) > 0}
              img={pkg.img}
              value={getSelectedPackageValue(pkg.id)}
              onAdd={() => onAdd(pkg.id)}
              onMinus={() => onMinus(pkg.id)}
              max={pkg.maxItem}
              cardInfo={isCurtainBooking}
              setShowItemInfo={() => setShowItemInfo(!showItemInfo)}
            />
          ))}
        </View>
      </View>
      <Dialog
        open={showItemInfo}
        onOpenChange={() => setShowItemInfo(!showItemInfo)}
      >
        <DialogContent className="rounded-lgp max-w-[337px] justify-stretch p-2">
          <Image src={CurtainItemInfo} width={319} height={214} />
        </DialogContent>
      </Dialog>
    </>
  );
};
